import { createToken, Lexer } from 'chevrotain';
import * as JT from './jinja.tokens.js';

// Define lexer modes
export const LexerModes = {
  NORMAL: 'normal',
  UNPARSED: 'unparsed',
  BINARYBLOB: 'binaryblob',
  CODESTREAM: 'codestream',
  MLCOMMENT: 'mlcomment',
  IGNORERESTOFLINE: 'ignorerestofline',
  JINJA_EXPR: JT.LexerModes.JINJA_EXPR,
  JINJA_STMT: JT.LexerModes.JINJA_STMT,
  JINJA_COMMENT: JT.LexerModes.JINJA_COMMENT
};

// Reserved keywords (longest first to avoid shadowing)
export const IncludeIfPresent = createToken({ name: 'IncludeIfPresent', pattern: /#includeIfPresent|#sinclude/ });
export const IncludeFunc = createToken({ name: 'IncludeFunc', pattern: /#includeFunc/ });
export const IncludeEtc = createToken({ name: 'IncludeEtc', pattern: /#includeEtc/ });
export const Include = createToken({ name: 'Include', pattern: /#include/ });

export const FoamFile = createToken({ name: 'FoamFile', pattern: /FoamFile/ });
export const Uniform = createToken({ name: 'Uniform', pattern: /uniform/ });
export const NonUniform = createToken({ name: 'NonUniform', pattern: /nonuniform/ });
export const Remove = createToken({ name: 'Remove', pattern: /#remove/ });
export const InputMode = createToken({ name: 'InputMode', pattern: /#inputMode/ });
export const Merge = createToken({ name: 'Merge', pattern: /merge/ });
export const Overwrite = createToken({ name: 'Overwrite', pattern: /overwrite/ });
export const Error = createToken({ name: 'Error', pattern: /error/ });
export const Warn = createToken({ name: 'Warn', pattern: /warn/ });
export const Protect = createToken({ name: 'Protect', pattern: /protect/ });

export const ReservedKeywords = {
  FoamFile,
  Uniform,
  NonUniform,
  IncludeIfPresent,
  IncludeFunc,
  IncludeEtc,
  Include,
  Remove,
  InputMode,
  Merge,
  Overwrite,
  Error,
  Warn,
  Protect
};

// Other tokens
export const Name = createToken({
  name: 'Name',
  pattern: /[a-zA-Z_][+\-<>(),.*|a-zA-Z_0-9&%:]*/,
  longer_alt: Object.values(ReservedKeywords)
});

export const IntegerLiteral = createToken({ name: 'IntegerLiteral', pattern: /-?\d+([uUlL]*)?/ });
export const FloatLiteral = createToken({ name: 'FloatLiteral', pattern: /-?(\d+(\.\d*)?([eE][+-]?\d+)?|\d+[eE][+-]?\d+)([lLfF])?/ });
export const IncludeString = createToken({ name: 'IncludeString', pattern: /"[^"\n]*"/ });
export const StringLiteral = createToken({ name: 'StringLiteral', pattern: /"([^\\\n]|(\\.))*?"/ });
export const Substitution = createToken({ name: 'Substitution', pattern: /\$[a-zA-Z_.:{][+\-<>(),.*|a-zA-Z_0-9&%:${}]*/ });

export const CodeStart = createToken({ name: 'CodeStart', pattern: /#\{/, push_mode: LexerModes.CODESTREAM });
export const CodeEnd = createToken({ name: 'CodeEnd', pattern: /#\}/, pop_mode: true });
export const Hash = createToken({ name: 'Hash', pattern: /#/ });

// Punctuation
export const LBrace = createToken({ name: 'LBrace', pattern: /\{/ });
export const RBrace = createToken({ name: 'RBrace', pattern: /\}/ });
export const LParen = createToken({ name: 'LParen', pattern: /\(/ });
export const RParen = createToken({ name: 'RParen', pattern: /\)/ });
export const Semi = createToken({ name: 'Semi', pattern: /;/ });
export const Comma = createToken({ name: 'Comma', pattern: /,/ });
export const LBracket = createToken({ name: 'LBracket', pattern: /\[/ });
export const RBracket = createToken({ name: 'RBracket', pattern: /\]/ });
export const Caret = createToken({ name: 'Caret', pattern: /\^/ });
export const Mul = createToken({ name: 'Mul', pattern: /\*/ });
export const Div = createToken({ name: 'Div', pattern: /\// });

// Mode-specific tokens
export const BinaryBlob = createToken({ name: 'BinaryBlob', pattern: /[^)]+/, line_breaks: true });
export const UnparsedChunk = createToken({ name: 'UnparsedChunk', pattern: /[^)]+/, line_breaks: true });
export const CodeStreamChunk = createToken({ name: 'CodeStreamChunk', pattern: /[^#]+/, line_breaks: true });
export const RestOfLine = createToken({ name: 'RestOfLine', pattern: /[^\n]+/, line_breaks: true });

// Whitespace and comments
export const WhiteSpace = createToken({ name: 'WhiteSpace', pattern: /[ \t\r\n]+/, group: Lexer.SKIPPED });
export const LineComment = createToken({ name: 'LineComment', pattern: /\/\/[^\n\r]*/, group: Lexer.SKIPPED });
export const MultiLineComment = createToken({ name: 'MultiLineComment', pattern: /\/\*[\s\S]*?\*\//, group: Lexer.SKIPPED });

export const allTokens = [
  WhiteSpace, LineComment, MultiLineComment,
  JT.JinjaExprStart, JT.JinjaIfStart, JT.JinjaElifStart, JT.JinjaElseStart, JT.JinjaEndifStart,
  JT.JinjaForStart, JT.JinjaEndforStart, JT.JinjaSetStart, JT.JinjaBlockStart, JT.JinjaEndblockStart,
  JT.JinjaExtendsStart, JT.JinjaIncludeStart, JT.JinjaMacroStart, JT.JinjaEndmacroStart, //  <<<< JINJA >>>  

  // Reserved keywords in correct order
  IncludeIfPresent, IncludeFunc, IncludeEtc, Include,
  FoamFile, Uniform, NonUniform, Remove, InputMode, Merge, Overwrite, Error, Warn, Protect,
  // Other literals and symbols
  FloatLiteral, IntegerLiteral, StringLiteral, Substitution,
  CodeStart, CodeEnd, Hash,
  LBrace, RBrace, LParen, RParen, Semi, Comma, LBracket, RBracket, Caret, Mul, Div,
  Name, IncludeString,
  JT.JinjaExprChunk, JT.JinjaStmtChunk, JT.JinjaExprEnd, JT.JinjaStmtEnd//  <<<< JINJA >>>
];

export const OpenFoamLexer = new Lexer(
  {
    modes: {
      [LexerModes.NORMAL]: allTokens,
      [LexerModes.UNPARSED]: [UnparsedChunk, RParen],
      [LexerModes.BINARYBLOB]: [BinaryBlob, RParen],
      [LexerModes.CODESTREAM]: [CodeStreamChunk, CodeEnd],
      [LexerModes.MLCOMMENT]: [MultiLineComment],
      [LexerModes.IGNORERESTOFLINE]: [RestOfLine],
      [LexerModes.JINJA_EXPR]: [JT.JinjaExprChunk, JT.JinjaExprEnd],
      [LexerModes.JINJA_STMT]: [JT.JinjaStmtChunk, JT.JinjaStmtEnd],
      [LexerModes.JINJA_COMMENT]: [JT.JinjaCommentChunk, JT.JinjaCommentEnd]
    },
    defaultMode: LexerModes.NORMAL,
    positionTracking: "full"
  }
);
